import { useState } from "react";
import { ReservationLogEntryContainerDto, ReservationRefund, ReservationRefundDto, ReservationState } from './types';
import { format } from "date-fns";
import { tryParseFloat } from '@/utils/utils';
import { RefundTypes } from "./RefundTypes/RefundType";
import { TaxDocumentTypes } from "./TaxDocumentTypes/TaxDocumentType";
import { deserializeReservationLogEntry } from "./ReservationLogEntry";

export function AdjustRefundDialogComponent({ reservationState, refund, close }: AdjustRefundDialogComponentProps) {

    const [refundDate, setRefundDate] = useState(format(new Date(), 'yyyy-MM-dd'));

    function refundDateChanged(e: React.ChangeEvent<HTMLInputElement>) {
        setRefundDate(e.target.value);
    }

    const [adjustAmount, setAdjustAmount] = useState(refund.Amount);
    const [isAdjustAmountFocused, setIsAdjustAmountFocused] = useState(false);

    function adjustAmountChanged(e: React.ChangeEvent<HTMLInputElement>) {
        let value = tryParseFloat(e.target.value, adjustAmount);
        setAdjustAmount(value);
    }

    const [adjustmentNote, setAdjustmentNote] = useState('');

    function adjustmentNoteChanged(e: React.ChangeEvent<HTMLTextAreaElement>) {
        setAdjustmentNote(e.target.value);
    }

    const [adjustingRefund, setAdjustingRefund] = useState(false);

    async function adjustRefund() {
        setAdjustingRefund(true);

        const request: AdjustRefundRequest = {
            effectiveDate: refundDate,
            amount: adjustAmount,
            note: adjustmentNote
        };

        const response = await fetch(
            `/reservations/${reservationState.ReservationId}/refunds/${refund.RefundId}/adjustments`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(request)
            });

        if (response.ok) {

            const adjustRefundResponse = await response.json() as AdjustRefundResponse;

            if (adjustRefundResponse.Refund) {
                reservationState.Refunds.replace(
                    refund,
                    {
                        CreatedDate: new Date(adjustRefundResponse.Refund.CreatedDate),
                        RefundId: adjustRefundResponse.Refund.RefundId,
                        RefundType: RefundTypes.deserializeRefundType(adjustRefundResponse.Refund.RefundType),
                        Amount: adjustRefundResponse.Refund.Amount,
                        Transactions: adjustRefundResponse.Refund.Transactions.map(t => ({
                            CreatedDate: new Date(t.CreatedDate),
                            EffectiveDate: t.EffectiveDate,
                            Amount: t.Amount,
                            Note: t.Note,
                            TaxDocument: TaxDocumentTypes.deserializeTaxDocumentType(t.TaxDocument),
                        }))
                    });
            }

            reservationState.LogEntries.clear();

            adjustRefundResponse.Logs
                .map(l => ({
                    LogEntryId: l.LogEntryId,
                    LogText: l.LogText,
                    LogData: deserializeReservationLogEntry(l.LogData),
                    CreatedBy: l.CreatedBy,
                    CreatedAt: l.CreatedAt
                }))
                .forEach(log => {
                    reservationState.LogEntries.add(log);
                });

            //reservationState.Refunds.remove(refund);

            //reservationState.Refunds.add({
            //    RefundId: refund.RefundId,
            //    RefundType: refund.RefundType,
            //    Amount: adjustAmount,
            //    Transactions: refund.Transactions,
            //})

            await (window as any).salesSchedule.refresh();
        }

        setAdjustingRefund(false);

        close?.();
    }

    const isValid = refundDate.length > 0 && adjustAmount >= 0;

    return (

        <div className="flex flex-col gap-2">
            <div>
                <table className="table">
                    <tbody>
                        <tr>
                            <th>Adjust Amount</th>
                            <td className="px-2 text-right" colSpan={2}>
                                <div className="flex flex-row place-content-end items-center gap-2 text-right">
                                    <input disabled={adjustingRefund} className="form-control w-32 text-right" type="number" min="0" step="0.01"
                                        value={isAdjustAmountFocused ? adjustAmount : adjustAmount.toFixed(2)}
                                        onChange={adjustAmountChanged}
                                        onFocus={() => setIsAdjustAmountFocused(true)}
                                        onBlur={() => {
                                            setIsAdjustAmountFocused(false);
                                            setAdjustAmount(Math.round((adjustAmount * 100)) / 100);
                                        }} />
                                    <div>THB</div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <th>Effective Date</th>
                            <td colSpan={2} className="place-items-end text-right">
                                <input disabled={adjustingRefund} type="date" className="form-control w-40" value={refundDate} onChange={refundDateChanged} />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div>
                <label>Note</label>
                <textarea disabled={adjustingRefund} className="form-control" value={adjustmentNote} onChange={adjustmentNoteChanged}></textarea>
            </div>
            <div className="place-self-end">
                <button className="btn btn-outline-contrast" onClick={adjustRefund} disabled={adjustingRefund || !isValid}>Adjust Refund</button>
            </div>
        </div>
    );
}

export interface AdjustRefundDialogComponentProps {
    reservationState: ReservationState;
    refund: ReservationRefund;
    close?: () => void;
}

export interface AdjustRefundRequest {
    effectiveDate: string;
    amount: number;
    note: string;
}

export interface AdjustRefundResponse {
    Refund: ReservationRefundDto;
    Logs: ReservationLogEntryContainerDto[];
}
